import { useCallback } from 'react';
import {
    type Index,
    type Result,
    type AsyncStateUpdate,
    type AsyncStateUpdateRequest,
    isOk,
} from '@synqo/frontend-component-library';

function getParentUpdater<TParent, TChild>(
    childUpdater: AsyncStateUpdateRequest<TChild>,
    childPropertyName: Index<TParent>,
): AsyncStateUpdateRequest<TParent> {
    return async (latestParent, saveParent) => {
        let updatedData: TParent = latestParent;
        const saveChild = async (d: TChild): Promise<Result<TChild>> => {
            const saveParentResult = await saveParent({
                ...latestParent,
                [childPropertyName]: d,
            });
            if (!isOk(saveParentResult)) {
                return { data: undefined, messages: saveParentResult.messages };
            } else {
                updatedData = saveParentResult.data;
                const updated = saveParentResult.data[childPropertyName] as TChild;
                return { data: updated, messages: saveParentResult.messages };
            }
        };

        const oldChild = latestParent[childPropertyName] as TChild;
        const updateChildResult = await childUpdater(oldChild, saveChild);

        if (!updateChildResult) {
            return undefined;
        } else if (!isOk(updateChildResult)) {
            return { data: undefined, messages: updateChildResult.messages };
        } else {
            return {
                data: {
                    ...updatedData,
                    [childPropertyName]: updateChildResult.data,
                },
                messages: updateChildResult.messages,
            };
        }
    };
}

export function useSaveNestedObject<TParent, TChild>(requestUpdateState: AsyncStateUpdate<TParent>) {
    return useCallback(
        async (childUpdater: AsyncStateUpdateRequest<TChild>, childPropertyName: Index<TParent>) => {
            const parentUpdater = getParentUpdater<TParent, TChild>(childUpdater, childPropertyName);
            return requestUpdateState(parentUpdater);
        },
        [requestUpdateState],
    );
}
